import GatekeeperOAuthCallback from "./oauth-callback";
import {
  GatekeeperOAuthClient,
  GatekeeperOAuthCallbackHandler,
} from "../../utils";
import { useCallback, useMemo } from "react";

interface GatekeeperOAuthCallbackPageProps {
  organizationSlug: string;
  setToken: (token: any) => void;
  handleOAuthCallback: GatekeeperOAuthCallbackHandler;
  successRedirectRoute: string;
}

/**
 * Reusable OAuth callback page component for both boa-settings and cotribute-academy
 *
 * This component handles the OAuth callback flow by:
 * 1. Setting up the OAuth client
 * 2. Handling the OAuth callback and token exchange
 * 3. Setting the JWT token in the session
 * 4. Redirecting to the appropriate route
 */
export const GatekeeperOAuthCallbackPage: React.FC<GatekeeperOAuthCallbackPageProps> =
  ({
    organizationSlug,
    setToken,
    handleOAuthCallback: handleOAuthCallbackProp,
    successRedirectRoute,
  }) => {
    // OAuth client
    const oAuthClient = useMemo(
      () =>
        typeof window !== "undefined" && organizationSlug
          ? new GatekeeperOAuthClient({
              clientId: process.env.NEXT_PUBLIC_GATEKEEPER_OAUTH_CLIENT_ID!,
              redirectUri: `${window.location.origin}/organization/${organizationSlug}/oauth/gatekeeper/callback`,
            })
          : null,
      [organizationSlug]
    );

    // OAuth callback handler
    const handleOAuthCallback: GatekeeperOAuthCallbackHandler = useCallback(
      async (params) => {
        // Exchange OAuth code for token via Settings API
        const result = await handleOAuthCallbackProp(params);

        // Validate and set token
        if (!result) {
          throw new Error("No token received from OAuth callback");
        }

        setToken(result);

        return result;
      },
      [handleOAuthCallbackProp, setToken]
    );

    // Error redirect
    const errorRedirectRoute = useMemo(
      () =>
        organizationSlug ? `/organization/${organizationSlug}/login` : "/",
      [organizationSlug]
    );

    if (!oAuthClient) {
      return <div>Loading...</div>;
    }

    return (
      <GatekeeperOAuthCallback
        oAuthClient={oAuthClient}
        onOAuthCallback={handleOAuthCallback}
        successRedirectRoute={successRedirectRoute}
        errorRedirectRoute={errorRedirectRoute}
      />
    );
  };
